/**
 * Auth-provider Refine.
 *
 * Отдельной системы авторизации у админки нет: тот же `/api/v1/auth/login`,
 * что и у игрового фронтенда. Разница в том, что сюда пускают только
 * пользователей с ролью moderator или admin — проверка делается и здесь,
 * чтобы не показывать пустой интерфейс, и на backend'е, где она обязательна.
 */
import type { AuthProvider } from "@refinedev/core";

import { ApiError, refreshAccessToken, request, setAccessToken } from "../lib/http";

interface AuthResponse {
  accessToken?: string;
  user?: { id: string; username: string; email: string; role: string };
}

type Identity = NonNullable<AuthResponse["user"]>;

const STAFF_ROLES = new Set(["ROLE_MODERATOR", "ROLE_ADMIN"]);

export const authProvider: AuthProvider = {
  login: async ({ email, password }: { email?: string; password?: string }) => {
    try {
      const data = await request<AuthResponse>("/api/v1/auth/login", {
        method: "POST",
        body: { login: email, password },
      });

      if (!data.user || !STAFF_ROLES.has(data.user.role)) {
        // Обычный игрок мог завести аккаунт и попробовать зайти сюда.
        // Токен не сохраняем: пусть он даже не выглядит как вход.
        setAccessToken(null);
        return {
          success: false,
          error: {
            name: "Нет доступа",
            message: "Панель доступна только модераторам и администраторам",
          },
        };
      }

      setAccessToken(data.accessToken ?? null);
      return { success: true, redirectTo: "/" };
    } catch (error) {
      return {
        success: false,
        error: {
          name: "Не удалось войти",
          message: error instanceof ApiError ? error.message : "Сервер недоступен",
        },
      };
    }
  },

  logout: async () => {
    try {
      await request("/api/v1/auth/logout", { method: "POST", body: {} });
    } catch {
      // Даже если запрос не дошёл, локально нужно выйти.
    }
    setAccessToken(null);
    return { success: true, redirectTo: "/login" };
  },

  check: async () => {
    // Access-токен живёт в памяти, поэтому после перезагрузки страницы
    // сессия восстанавливается по httpOnly refresh-cookie.
    if (!(await refreshAccessToken())) {
      return { authenticated: false, redirectTo: "/login" };
    }

    try {
      const user = await request<Identity>("/api/v1/auth/me");
      if (!STAFF_ROLES.has(user.role)) {
        return { authenticated: false, redirectTo: "/login", logout: true };
      }
      return { authenticated: true };
    } catch {
      return { authenticated: false, redirectTo: "/login" };
    }
  },

  getIdentity: async () => {
    try {
      const user = await request<Identity>("/api/v1/auth/me");
      return { ...user, name: user.username };
    } catch {
      return null;
    }
  },

  getPermissions: async () => {
    try {
      const user = await request<Identity>("/api/v1/auth/me");
      return user.role;
    } catch {
      return null;
    }
  },

  onError: async (error) => {
    if (error instanceof ApiError && error.status === 401) {
      return { logout: true, redirectTo: "/login", error };
    }
    return { error };
  },
};
